import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { Calendar, Clock, User, ArrowLeft, Share2, Facebook, Twitter, Linkedin, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import { getStaticBlog } from '@/data/staticBlogData';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BlogPostPage = () => {
  const { slug } = useParams();
  // Initialize with static data for pre-rendering
  const staticBlog = getStaticBlog(slug);
  const [blog, setBlog] = useState(staticBlog);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(!staticBlog);

  useEffect(() => {
    fetchBlog();
    fetchRelatedBlogs();
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs/${slug}`);
      setBlog(response.data);
    } catch (error) {
      console.error('Error fetching blog:', error);
      // Keep static data if API fails
      if (!blog && staticBlog) {
        setBlog(staticBlog);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchRelatedBlogs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/blogs`);
      const otherBlogs = response.data.filter(b => b.slug !== slug).slice(0, 3);
      setRelatedBlogs(otherBlogs);
    } catch (error) {
      console.error('Error fetching related blogs:', error);
    }
  };

  const openWhatsApp = () => {
    window.open('https://wa.me/919958848998?text=Hello%20Zafco,%20I%20need%20help%20with%20overseas%20recruitment', '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-zafco-blue border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Blog post not found</h1>
        <Link to="/blogs">
          <Button>Back to Blogs</Button>
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="blog-post-page">
      {/* SEO Meta Tags */}
      <Helmet defer={false}>
        <title>{blog.title} | Zafco HR Blog</title>
        <meta name="description" content={blog.excerpt} />
        <meta property="og:title" content={blog.title} />
        <meta property="og:description" content={blog.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://zafco.in/blogs/${slug}`} />
        <meta property="og:image" content={blog.image} />
        <link rel="canonical" href={`https://zafco.in/blogs/${slug}`} />
        
        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": blog.title,
            "description": blog.excerpt,
            "image": blog.image,
            "url": `https://zafco.in/blogs/${slug}`,
            "author": {
              "@type": "Organization",
              "@id": "https://zafco.in/#organization",
              "name": blog.author,
              "url": "https://zafco.in/"
            },
            "publisher": {
              "@type": "Organization",
              "@id": "https://zafco.in/#organization",
              "name": "Zafco Human Resource Management",
              "logo": {
                "@type": "ImageObject",
                "url": "https://zafco.in/zafco-logo.png",
                "width": 600,
                "height": 60
              }
            },
            "datePublished": blog.date,
            "dateModified": blog.updated_at || blog.date,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://zafco.in/blogs/${slug}`
            }
          })}
        </script>
      </Helmet>
      
      <PageHeader 
        title={blog.title}
        breadcrumbs={[
          { label: 'Blogs', path: '/blogs' },
          { label: blog.category }
        ]}
      />

      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main Content */}
            <motion.article 
              className="lg:col-span-2"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Featured Image */}
              <div className="rounded-2xl overflow-hidden mb-8">
                <img
                  src={blog.image}
                  alt={blog.title}
                  className="w-full h-64 md:h-96 object-cover"
                />
              </div>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-gray-500 mb-8 pb-8 border-b">
                <span className="flex items-center gap-2">
                  <User className="w-4 h-4" />
                  {blog.author}
                </span>
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {blog.date}
                </span>
                <span className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  {blog.read_time}
                </span>
                <span className="px-3 py-1 bg-zafco-blue/10 text-zafco-blue text-sm font-semibold rounded-full">
                  {blog.category}
                </span>
              </div>

              {/* Content */}
              <div className="prose prose-lg max-w-none prose-headings:font-montserrat prose-headings:text-zafco-blue prose-a:text-zafco-teal prose-strong:text-zafco-blue">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => (
                      <h2 className="text-2xl font-bold text-zafco-blue mt-8 mb-4">{children}</h2>
                    ),
                    h2: ({ children }) => (
                      <h2 className="text-2xl font-bold text-zafco-blue mt-8 mb-4">{children}</h2>
                    ),
                    h3: ({ children }) => (
                      <h3 className="text-xl font-bold text-zafco-blue mt-6 mb-3">{children}</h3>
                    ),
                    p: ({ children }) => (
                      <p className="text-gray-700 leading-relaxed mb-4">{children}</p>
                    ),
                    ul: ({ children }) => (
                      <ul className="list-disc pl-6 mb-4 space-y-2 text-gray-700">{children}</ul>
                    ),
                    ol: ({ children }) => (
                      <ol className="list-decimal pl-6 mb-4 space-y-2 text-gray-700">{children}</ol>
                    ),
                    table: ({ children }) => (
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full border-collapse bg-white rounded-xl overflow-hidden shadow-sm">
                          {children}
                        </table>
                      </div>
                    ),
                    th: ({ children }) => (
                      <th className="bg-zafco-blue text-white px-4 py-3 text-left font-semibold">
                        {children}
                      </th>
                    ),
                    td: ({ children }) => (
                      <td className="border-b border-gray-100 px-4 py-3">{children}</td>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold text-zafco-blue">{children}</strong>
                    ),
                  }}
                >
                  {blog.content}
                </ReactMarkdown>
              </div>

              {/* CTA */}
              <div className="mt-12 bg-gradient-to-br from-zafco-blue to-zafco-teal rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold font-montserrat mb-4">
                  Ready to Start Your Global Career?
                </h3>
                <p className="text-white/80 mb-6">
                  Our experts at Zafco Human Resource Management are here to help you achieve your overseas career goals.
                </p>
                <Button 
                  onClick={openWhatsApp}
                  className="bg-gradient-to-r from-zafco-gold to-zafco-orange text-white font-semibold px-8"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Get Free Assessment
                </Button>
              </div>

              {/* Share */}
              <div className="mt-8 pt-8 border-t">
                <div className="flex items-center gap-4">
                  <span className="font-semibold text-gray-700">Share this article:</span>
                  <div className="flex gap-2">
                    {[
                      { Icon: Facebook, label: 'Facebook' },
                      { Icon: Twitter,  label: 'Twitter'  },
                      { Icon: Linkedin, label: 'LinkedIn' },
                    ].map(({ Icon, label }) => (
                      <button
                        key={label}
                        aria-label={`Share on ${label}`}
                        className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-zafco-blue hover:text-white transition-colors"
                      >
                        <Icon className="w-5 h-5" />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </motion.article>

            {/* Sidebar */}
            <aside className="lg:col-span-1">
              <motion.div
                className="sticky top-24 space-y-8"
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                {/* Consultation Form */}
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100">
                  <h3 className="text-xl font-bold text-zafco-blue font-montserrat mb-4">
                    Free Consultation
                  </h3>
                  <ConsultationForm variant="compact" />
                </div>

                {/* Related Posts */}
                {relatedBlogs.length > 0 && (
                  <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
                    <h3 className="text-lg font-bold text-zafco-blue font-montserrat mb-4">
                      Related Articles
                    </h3>
                    <div className="space-y-4">
                      {relatedBlogs.map((related) => (
                        <Link 
                          key={related.id} 
                          to={`/blogs/${related.slug}`}
                          className="block group"
                        >
                          <div className="flex gap-4">
                            <img
                              src={related.image}
                              alt={related.title}
                              className="w-20 h-16 object-cover rounded-lg flex-shrink-0"
                            />
                            <div>
                              <h4 className="text-sm font-semibold text-gray-800 line-clamp-2 group-hover:text-zafco-blue transition-colors">
                                {related.title}
                              </h4>
                              <span className="text-xs text-gray-500">{related.date}</span>
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}

                {/* Back to Blogs */}
                <Link to="/blogs">
                  <Button variant="outline" className="w-full">
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to All Blogs
                  </Button>
                </Link>
              </motion.div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogPostPage;
